import React, { useState, useEffect } from 'react';
import { useUserService } from '../hooks/useUserService';
import { useAuth } from '../contexts/AuthContext';
import { useWatch } from '../contexts/WatchContext';
import { useNavigate } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { Eye, Trash2, Key, Search, UserCog, Mail, Settings, Infinity } from 'lucide-react';
import AdminBadge from './UI/AdminBadge';
import LoadingSpinner from './UI/LoadingSpinner';
import deviceService from '../utils/services/DeviceService';
import screenService from '../utils/services/ScreenService';
import firebaseService from '../utils/services/FirebaseService';
import UserService from '../utils/services/UserService';
import EmailButton from './UI/EmailButton';
import BulkEmailModal from './UI/BulkEmailModal';
import SuccessModal from './UI/SuccessModal';
import DeviceOverrideModal from './UI/DeviceOverrideModal';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { hasAdminPrivileges } from '@/utils/adminUtils';

const UserServiceTest = () => {
  const { currentUser, currentUserData } = useAuth();
  const { startWatching, isWatching, watchedUser } = useWatch();
  const navigate = useNavigate();
  
  // Admin-only access check
  if (!hasAdminPrivileges(currentUserData)) {
    return <Navigate to="/dashboard" replace />
  }
  const {
    users,
    loading,
    error,
    stats,
    getAllUsers,
    getCurrentUser,
    updateUser,
    deleteUser,
    extendTrial,
    getUserStats,
    clearError,
    clearCache,
    getCacheStats,
    logIndexInstructions,
    debugGetAllUsers,
    adminUsers,
    demoUsers,
    testUsers,
    expiredTrialUsers,
    activeTrialUsers,
    // Email subscription methods
    getUserEmailSubscription,
    setUserEmailSubscription,
    subscribeUserToEmails,
    unsubscribeUserFromEmails,
    getSubscribedUsers,
    getUnsubscribedUsers
  } = useUserService();

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [userDevices, setUserDevices] = useState({});
  const [userScreens, setUserScreens] = useState({});
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [screensLoading, setScreensLoading] = useState(false);
  const [sortField, setSortField] = useState('user');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [passwordResetStatus, setPasswordResetStatus] = useState({ type: '', message: '' });
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleUser, setRoleUser] = useState(null);
  const [roleStatus, setRoleStatus] = useState({ type: '', message: '' });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteUserData, setDeleteUserData] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState({ type: '', message: '' });
  const [showEmailSubscriptionModal, setShowEmailSubscriptionModal] = useState(false);
  const [emailSubscriptionUser, setEmailSubscriptionUser] = useState(null);
  const [emailSubscriptionStatus, setEmailSubscriptionStatus] = useState({ type: '', message: '' });
  const [showDeviceOverrideModal, setShowDeviceOverrideModal] = useState(false);
  const [deviceOverrideUser, setDeviceOverrideUser] = useState(null);
  
  // Google Analytics tracking
  const { trackUserManagement, trackEmailAction, trackError } = useGoogleAnalytics();

  // Load devices and screens for all users
  useEffect(() => {
    const loadAllUserData = async () => {
      if (!users.length) return;
      
      setDevicesLoading(true);
      setScreensLoading(true);
      const devicesMap = {};
      const screensMap = {};
      
      try {
        // Load devices and screens for each user in parallel
        const dataPromises = users.map(async (user) => {
          try {
            // Load devices using getUserDevices for consistency with watched user functionality
            const devices = await deviceService.getUserDevices(user.key, true);
            devicesMap[user.key] = devices;
            
            // Load screens
            const screens = await screenService.getUserScreens(user.key, true);
            screensMap[user.key] = screens;
          } catch (error) {
            console.error(`Error loading data for user ${user.key}:`, error);
            devicesMap[user.key] = [];
            screensMap[user.key] = [];
          }
        });
        
        await Promise.all(dataPromises);
        setUserDevices(devicesMap);
        setUserScreens(screensMap);
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setDevicesLoading(false);
        setScreensLoading(false);
      }
    };

    loadAllUserData();
  }, [users]);

  // Get last active time for a user based on their devices
  const getUserLastActive = (userId) => {
    const devices = userDevices[userId] || [];
    if (devices.length === 0) return 'Never';
    
    // Find the most recent lastAccess timestamp
    const lastAccessTimes = devices
      .map(device => device.lastAccess || 0)
      .filter(time => time > 0);
    
    if (lastAccessTimes.length === 0) return 'Never';
    
    const mostRecent = Math.max(...lastAccessTimes);
    return new Date(mostRecent).toLocaleString();
  };

  // Get last active timestamp for sorting
  const getUserLastActiveTimestamp = (userId) => {
    const devices = userDevices[userId] || [];
    if (devices.length === 0) return 0;
    
    const lastAccessTimes = devices
      .map(device => device.lastAccess || 0)
      .filter(time => time > 0);
    
    if (lastAccessTimes.length === 0) return 0;
    
    return Math.max(...lastAccessTimes);
  };

  // Get number of online devices for a user
  const getUserOnlineDeviceCount = (userId) => {
    const devices = userDevices[userId] || [];
    return devices.filter(device => device.isOnline).length;
  };

  // Get trial expiration date
  const getTrialExpirationDate = (user) => {
    if (user.trialData && user.trialData.endDate) {
      const expirationDate = new Date(user.trialData.endDate);
      return expirationDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return 'Unknown';
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };


  // Filter and sort users based on search term and sort settings
  const getSortedUsers = () => {
    if (!users.length) return [];

    // First filter users based on search term
    let filteredUsers = users;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredUsers = users.filter(user => {
        const displayName = (user.displayNameOrEmail || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        
        return displayName.includes(searchLower) || 
               username.includes(searchLower) || 
               email.includes(searchLower);
      });
    }

    // Then sort the filtered users
    return filteredUsers.sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'user') {
        aValue = (a.displayNameOrEmail || '').toLowerCase();
        bValue = (b.displayNameOrEmail || '').toLowerCase();
      } else if (sortField === 'lastActive') {
        aValue = getUserLastActiveTimestamp(a.key);
        bValue = getUserLastActiveTimestamp(b.key);
      } else if (sortField === 'onlineDevices') {
        aValue = getUserOnlineDeviceCount(a.key);
        bValue = getUserOnlineDeviceCount(b.key);
      } else if (sortField === 'devices') {
        aValue = (userDevices[a.key] || []).length;
        bValue = (userDevices[b.key] || []).length;
      } else if (sortField === 'screens') {
        aValue = (userScreens[a.key] || []).length;
        bValue = (userScreens[b.key] || []).length;
      } else {
        return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const handleUpdateUser = async (uid, updateData) => {
    try {
      await updateUser(uid, updateData);
      // Refresh the users list to show updated data
      await getAllUsers(false);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = (user) => {
    setDeleteUserData(user);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserData) return;
    
    try {
      setDeleteStatus({ type: 'loading', message: 'Deleting user...' });
      
      await deleteUser(deleteUserData.key, deleteUserData.username, deleteUserData.displayName);
      
                   setDeleteStatus({ type: 'success', message: 'User deleted successfully!' });

             // Track user deletion
             trackUserManagement('delete_user', deleteUserData.isAdmin ? 'admin' : 
                                               deleteUserData.testUser ? 'test' : 
                                               deleteUserData.demoUser ? 'demo' : 'regular');

             // Refresh the users list to show updated data
             await getAllUsers(false);
      
      // Close modal after a delay
      setTimeout(() => {
        closeDeleteConfirmModal();
      }, 2000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setDeleteStatus({ type: 'error', message: `Error deleting user: ${error.message}` });
    }
  };

  const closeDeleteConfirmModal = () => {
    setShowDeleteConfirmModal(false);
    setDeleteUserData(null);
    setDeleteStatus({ type: '', message: '' });
  };

  const handleWatchUser = (user) => {
    startWatching(user);
  };

  const handlePasswordReset = async (user) => {
    setPasswordResetUser(user);
    setShowPasswordResetModal(true);
  };

  const confirmPasswordReset = async () => {
    if (!passwordResetUser) return;
    
    try {
      setPasswordResetStatus({ type: 'loading', message: 'Sending password reset email...' });
      
      await firebaseService.sendPasswordResetEmail(passwordResetUser.username || passwordResetUser.email);
      
      setPasswordResetStatus({ type: 'success', message: 'Password reset email sent successfully!' });
      
      // Close modal after a delay
      setTimeout(() => {
        closePasswordResetModal();
      }, 2000);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setPasswordResetStatus({ type: 'error', message: `Error sending password reset email: ${error.message}` });
    }
  };

  const closePasswordResetModal = () => {
    setShowPasswordResetModal(false);
    setPasswordResetUser(null);
    setPasswordResetStatus({ type: '', message: '' });
  };

  const handleRoleChange = (user) => {
    setRoleUser(user);
    setShowRoleModal(true);
  };

  const confirmRoleChange = async (newRole) => {
    if (!roleUser) return;
    
    try {
      setRoleStatus({ type: 'loading', message: 'Updating user role...' });
      
      let updateData;
      
      if (newRole === 'regular') {
        // For regular users, set all role flags to false
        updateData = {
          isAdmin: false,
          testUser: false,
          demoUser: false
        };
      } else {
        // For specific roles, set only that role to true and others to false
        updateData = {
          isAdmin: newRole === 'admin',
          testUser: newRole === 'test',
          demoUser: newRole === 'demo'
        };
      }
      
      await updateUser(roleUser.key, updateData);
      setRoleStatus({ type: 'success', message: `User role updated to ${newRole}` });
      
      // Refresh the users list to show updated data
      await getAllUsers(false);
    } catch (error) {
      console.error('Error updating user role:', error);
      setRoleStatus({ type: 'error', message: `Error updating user role: ${error.message}` });
    }
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setRoleUser(null);
    setRoleStatus({ type: '', message: '' });
  };

  const handleEmailSubscription = (user) => {
    setEmailSubscriptionUser(user);
    setShowEmailSubscriptionModal(true);
  };

  const confirmEmailSubscription = async (subscribe) => {
    if (!emailSubscriptionUser) return;

    try {
      setEmailSubscriptionStatus({ type: 'loading', message: `${subscribe ? 'Subscribing' : 'Unsubscribing'} user...` });

      if (subscribe) {
        await subscribeUserToEmails(emailSubscriptionUser.key);
      } else {
        await unsubscribeUserFromEmails(emailSubscriptionUser.key);
      }

             setEmailSubscriptionStatus({ 
         type: 'success', 
         message: `User successfully ${subscribe ? 'subscribed' : 'unsubscribed'} from emails!` 
       });

       // Track email subscription change
       trackUserManagement('email_subscription_change', subscribe ? 'subscribed' : 'unsubscribed');

       // Refresh the users list to show updated data
       await getAllUsers(false);

      // Close modal after a delay
      setTimeout(() => {
        closeEmailSubscriptionModal();
      }, 2000);
    } catch (error) {
      console.error('Error updating email subscription:', error);
      setEmailSubscriptionStatus({ 
        type: 'error', 
        message: `Error ${subscribe ? 'subscribing' : 'unsubscribing'} user: ${error.message}` 
      });
    }
  };

  const closeEmailSubscriptionModal = () => {
    setShowEmailSubscriptionModal(false);
    setEmailSubscriptionUser(null);
    setEmailSubscriptionStatus({ type: '', message: '' });
  };

  const handleDeviceOverride = (user) => {
    setDeviceOverrideUser(user);
    setShowDeviceOverrideModal(true);
  };

  const closeDeviceOverrideModal = () => {
    setShowDeviceOverrideModal(false);
    setDeviceOverrideUser(null);
  };

  const handleDeviceOverrideSuccess = () => {
    // Refresh the users list to show updated data
    getAllUsers(false);
  };

  // Handle user selection for bulk email
  const handleUserSelection = (userId) => {
    const newSelectedUsers = new Set(selectedUsers);
    if (newSelectedUsers.has(userId)) {
      newSelectedUsers.delete(userId);
    } else {
      newSelectedUsers.add(userId);
    }
    setSelectedUsers(newSelectedUsers);
  };

  // Handle select all users
  const handleSelectAll = () => {
    if (selectedUsers.size === sortedUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(sortedUsers.map(user => user.key)));
    }
  };

  // Handle bulk email
  const handleBulkEmail = () => {
    if (selectedUsers.size === 0) {
      alert('Please select at least one user to send email to.');
      return;
    }
    setShowBulkEmailModal(true);
  };

  // Handle bulk email send
  const handleBulkEmailSend = async (subject, emailBody, subscribedUsers = null) => {
    try {
      // Use subscribedUsers if provided, otherwise fall back to selectedUsers
      const userIdsToSend = subscribedUsers ? subscribedUsers : Array.from(selectedUsers);
      
      // Convert plain text to HTML format for proper email rendering
      const htmlContent = {
        title: subject,
        text: emailBody.replace(/\n/g, '<br>') // Convert line breaks to HTML
      };
      
      await firebaseService.sendEmailByAdmin(userIdsToSend, subject, emailBody, htmlContent);
      setSuccessMessage(`Email sent successfully to ${userIdsToSend.length} users!`);
      setShowSuccessModal(true);
      setShowBulkEmailModal(false);
      setSelectedUsers(new Set());
      
      // Track bulk email action
      trackEmailAction('bulk_email_sent', userIdsToSend.length);
    } catch (error) {
      console.error('Error sending bulk email:', error);
      
      // Track email error
      trackError('bulk_email_error', error.message);
      
      alert(`Failed to send email: ${error.message}`);
    }
  };

  if (!UserService.isUserAdmin(currentUserData)) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">User Service Test</h2>
        <p className="text-gray-600">Admin access required to test user management functions.</p>
      </div>
    );
  }

  const sortedUsers = getSortedUsers();

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">User Service Test</h2>
        <AdminBadge />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
          <button
            onClick={clearError}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Clear Error
          </button>
        </div>
      )}

      {/* Debug Controls */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-3">Debug Controls</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => getAllUsers(false)}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            Refresh Users
          </button>
          <button
            onClick={() => getUserStats(false)}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
          >
            Refresh Stats
          </button>
          <button
            onClick={clearCache}
            className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600"
          >
            Clear Cache
          </button>
          <button
            onClick={() => getCacheStats()}
            className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
          >
            Cache Stats
          </button>
          <button
            onClick={logIndexInstructions}
            className="px-3 py-1 bg-indigo-500 text-white rounded text-sm hover:bg-indigo-600"
          >
            Index Instructions
          </button>
          {selectedUsers.size > 0 && (
            <button
              onClick={handleBulkEmail}
              className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-1"
            >
              <Mail className="w-4 h-4" />
              Email {selectedUsers.size} Users
            </button>
          )}
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-3">User Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium">Total:</span> {stats.total}
            </div>
            <div>
              <span className="font-medium">Admins:</span> {stats.admins}
            </div>
            <div>
              <span className="font-medium">Demo:</span> {stats.demoUsers}
            </div>
            <div>
              <span className="font-medium">Test:</span> {stats.testUsers}
            </div>
            <div>
              <span className="font-medium">Trial Active:</span> {stats.trialActive}
            </div>
            <div>
              <span className="font-medium">Trial Expired:</span> {stats.trialExpired}
            </div>
            <div>
              <span className="font-medium">Email Unsubscribed:</span> {stats.emailUnsubscribed}
            </div>
          </div>
        </div>
      )}

      {/* Search Box */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Users ({getSortedUsers().length} of {users.length})</h3>
          {(devicesLoading || screensLoading) && (
            <div className="flex items-center text-sm text-gray-600">
              <LoadingSpinner size="sm" />
              <span className="ml-2">Loading user data...</span>
            </div>
          )}
        </div>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === sortedUsers.length && sortedUsers.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('user')}
                  >
                    <div className="flex items-center">
                      User
                      {sortField === 'user' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('lastActive')}
                  >
                    <div className="flex items-center">
                      Last Active
                      {sortField === 'lastActive' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('devices')}
                  >
                    <div className="flex items-center">
                      Devices
                      {sortField === 'devices' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('onlineDevices')}
                  >
                    <div className="flex items-center">
                      Online Devices
                      {sortField === 'onlineDevices' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel Devices</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('screens')}
                  >
                    <div className="flex items-center">
                      Screens
                      {sortField === 'screens' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedUsers.map((user) => {
                  const devices = userDevices[user.key] || [];
                  const screens = userScreens[user.key] || [];
                  const numDevices = devices.length;
                  const numScreens = screens.length;
                  return (
                    <tr
                      key={user.key}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        selectedUser?.key === user.key ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setSelectedUser(user)}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.key)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleUserSelection(user.key);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.displayNameOrEmail}
                            </div>
                            <div className="text-xs text-gray-500">ID: {user.key}</div>
                            {user.username && (
                              <div className="text-xs text-gray-400">{user.username}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <span 
                            className={`px-2 py-1 text-xs rounded ${
                              user.status === 'NoVer' ? 'bg-red-100 text-red-800 border border-red-300' :
                              user.statusColor === 'green' ? 'bg-green-100 text-green-800' :
                              user.statusColor === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}
                            title={user.status === 'NoVer' ? 'Email not verified' : ''}
                          >
                            {user.status}
                          </span>
                          {UserService.isUserAdmin(user) && (
                            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                              Admin
                            </span>
                          )}
                          {UserService.isUserDemo(user) && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                              Demo
                            </span>
                          )}
                          {UserService.isUserTest(user) && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                              Test
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {getUserLastActive(user.key)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{numDevices}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {(() => {
                            const onlineCount = getUserOnlineDeviceCount(user.key);
                            const totalCount = numDevices;
                            if (totalCount === 0) {
                              return (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400 font-medium">0</span>
                                  <span className="text-xs text-gray-400">(no devices)</span>
                                </div>
                              );
                            } else if (onlineCount === 0) {
                              return (
                                <div className="flex items-center gap-1">
                                  <span className="text-red-600 font-medium">0</span>
                                  <span className="text-xs text-red-400">offline</span>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex items-center gap-1" title={`${onlineCount} of ${totalCount} devices online`}>
                                  <span className="text-green-600 font-medium">{onlineCount}</span>
                                  <span className="text-xs text-green-400">online</span>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {(() => {
                            const limitedDevices = devices.filter(device => device.limited).length;
                            if (limitedDevices === 0) {
                              return (
                                <div className="flex items-center gap-1">
                                  <span className="text-green-600 font-medium">0</span>
                                  <span className="text-xs text-gray-400">(none)</span>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex items-center gap-1" title={`${limitedDevices} device(s) are channel devices`}>
                                  <span className="text-orange-600 font-medium">{limitedDevices}</span>
                                  <span className="text-xs text-orange-400">channel</span>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{numScreens}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {/* Row 1: Primary Actions */}
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWatchUser(user);
                              }}
                              className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                              title="Watch this user's screens, devices, and schedules"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <EmailButton
                              recipientEmail={user.username || user.email || ''}
                              recipientUid={user.key}
                              user={user}
                              className="p-1.5 bg-blue-400 text-white rounded hover:bg-blue-500 transition-colors"
                              variant="ghost"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePasswordReset(user);
                              }}
                              className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              title="Send password reset email"
                            >
                              <Key className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRoleChange(user);
                              }}
                              className="p-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                              title="Change user role"
                            >
                              <UserCog className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          
                          {/* Row 2: Secondary Actions */}
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEmailSubscription(user);
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                user.emailUnsubscribed 
                                  ? 'bg-orange-500 text-white hover:bg-orange-600' 
                                  : 'bg-green-500 text-white hover:bg-green-600'
                              }`}
                              title={`${user.emailUnsubscribed ? 'Subscribe' : 'Unsubscribe'} user from emails`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeviceOverride(user);
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                user.deviceOverride 
                                  ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                                  : 'bg-gray-500 text-white hover:bg-gray-600'
                              }`}
                              title={`${user.deviceOverride ? 'Modify' : 'Set'} device override`}
                            >
                              <Settings className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteUser(user);
                              }}
                              className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                              title="Delete this user"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filtered Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold mb-2">Admin Users ({adminUsers.length})</h4>
          <div className="space-y-1 text-sm">
            {adminUsers.map(user => (
              <div key={user.key} className="p-2 bg-purple-50 rounded">
                <div>{user.displayNameOrEmail}</div>
                <div className="text-xs text-gray-500">Last Active: {getUserLastActive(user.key)}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Test Users ({testUsers.length})</h4>
          <div className="space-y-1 text-sm">
            {testUsers.map(user => (
              <div key={user.key} className="p-2 bg-gray-50 rounded">
                <div>{user.displayNameOrEmail}</div>
                <div className="text-xs text-gray-500">Last Active: {getUserLastActive(user.key)}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Demo Users ({demoUsers.length})</h4>
          <div className="space-y-1 text-sm">
            {demoUsers.map(user => (
              <div key={user.key} className="p-2 bg-blue-50 rounded">
                <div>{user.displayNameOrEmail}</div>
                <div className="text-xs text-gray-500">Last Active: {getUserLastActive(user.key)}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Active Trials ({activeTrialUsers.length})</h4>
          <div className="space-y-1 text-sm">
            {activeTrialUsers.map(user => (
              <div key={user.key} className="p-2 bg-green-50 rounded">
                <div>{user.displayNameOrEmail} ({user.trialDaysRemaining} days)</div>
                <div className="text-xs text-gray-500">Last Active: {getUserLastActive(user.key)}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Expired Trials ({expiredTrialUsers.length})</h4>
          <div className="space-y-1 text-sm">
            {expiredTrialUsers.map(user => (
              <div key={user.key} className="p-2 bg-red-50 rounded">
                <div>{user.displayNameOrEmail}</div>
                <div className="text-xs text-gray-500">Trial Expired: {getTrialExpirationDate(user)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Role Management Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Change User Role
              </h3>
              <button
                onClick={closeRoleModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {roleUser && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  User: <span className="font-medium">{roleUser.displayNameOrEmail}</span>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Email: <span className="font-medium">{roleUser.username}</span>
                </p>
                                 <p className="text-sm text-gray-600">
                   Current Role: <span className="font-medium">
                     {UserService.isUserAdmin(roleUser) ? 'Admin' : 
                      UserService.isUserTest(roleUser) ? 'Test User' : 
                      UserService.isUserDemo(roleUser) ? 'Demo User' : 'Regular User'}
                   </span>
                 </p>
              </div>
            )}

            {roleStatus.type === 'loading' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-blue-800">{roleStatus.message}</span>
                </div>
              </div>
            )}

            {roleStatus.type === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{roleStatus.message}</p>
              </div>
            )}

            {roleStatus.type === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{roleStatus.message}</p>
              </div>
            )}

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Select New Role:</p>
              <div className="space-y-2">
                {!UserService.isUserAdmin(roleUser) && (
                  <button
                    onClick={() => confirmRoleChange('admin')}
                    disabled={roleStatus.type === 'loading'}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-purple-700">👑 Admin</div>
                    <div className="text-xs text-gray-500">Full system access and management capabilities</div>
                  </button>
                )}
                {!UserService.isUserTest(roleUser) && (
                  <button
                    onClick={() => confirmRoleChange('test')}
                    disabled={roleStatus.type === 'loading'}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-gray-700">🧪 Test User</div>
                    <div className="text-xs text-gray-500">Testing and development access</div>
                  </button>
                )}
                {!UserService.isUserDemo(roleUser) && (
                  <button
                    onClick={() => confirmRoleChange('demo')}
                    disabled={roleStatus.type === 'loading'}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-blue-700">🎯 Demo User</div>
                    <div className="text-xs text-gray-500">Demonstration and presentation access</div>
                  </button>
                )}
                {(UserService.isUserAdmin(roleUser) || UserService.isUserTest(roleUser) || UserService.isUserDemo(roleUser)) && (
                  <button
                    onClick={() => confirmRoleChange('regular')}
                    disabled={roleStatus.type === 'loading'}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-green-700">👤 Regular User</div>
                    <div className="text-xs text-gray-500">Standard user access</div>
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              {roleStatus.type === 'success' || roleStatus.type === 'error' ? (
                <button
                  onClick={closeRoleModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : (
                <button
                  onClick={closeRoleModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Password Reset
              </h3>
              <button
                onClick={closePasswordResetModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {passwordResetUser && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  User: <span className="font-medium">{passwordResetUser.displayNameOrEmail}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Email: <span className="font-medium">{passwordResetUser.username}</span>
                </p>
              </div>
            )}

            {passwordResetStatus.type === 'loading' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-blue-800">{passwordResetStatus.message}</span>
                </div>
              </div>
            )}

            {passwordResetStatus.type === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{passwordResetStatus.message}</p>
              </div>
            )}

            {passwordResetStatus.type === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{passwordResetStatus.message}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              {passwordResetStatus.type === 'success' ? (
                <button
                  onClick={closePasswordResetModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : passwordResetStatus.type === 'error' ? (
                <button
                  onClick={closePasswordResetModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={closePasswordResetModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmPasswordReset}
                    disabled={passwordResetStatus.type === 'loading'}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send Reset Email
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <BulkEmailModal
          isOpen={showBulkEmailModal}
          onClose={() => setShowBulkEmailModal(false)}
          onSend={handleBulkEmailSend}
          selectedUsers={Array.from(selectedUsers)}
          users={users}
          loading={false}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message={successMessage}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm User Deletion
              </h3>
              <button
                onClick={closeDeleteConfirmModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {deleteUserData && (
              <div className="mb-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-red-800 font-medium">Warning: This action cannot be undone!</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-2">
                  <strong>User:</strong> {deleteUserData.displayNameOrEmail}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Email:</strong> {deleteUserData.username}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Role:</strong> {UserService.isUserAdmin(deleteUserData) ? 'Admin' : 
                                        UserService.isUserTest(deleteUserData) ? 'Test' : 
                                        UserService.isUserDemo(deleteUserData) ? 'Demo' : 'Regular'}
                </p>
                
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                  This will permanently delete the user account and all associated data including devices, screens, and schedules.
                </p>
              </div>
            )}

            {deleteStatus.type === 'loading' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-blue-800">{deleteStatus.message}</span>
                </div>
              </div>
            )}

            {deleteStatus.type === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{deleteStatus.message}</p>
              </div>
            )}

            {deleteStatus.type === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{deleteStatus.message}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              {deleteStatus.type === 'success' ? (
                <button
                  onClick={closeDeleteConfirmModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : deleteStatus.type === 'error' ? (
                <button
                  onClick={closeDeleteConfirmModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={closeDeleteConfirmModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteUser}
                    disabled={deleteStatus.type === 'loading'}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete User
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Subscription Modal */}
      {showEmailSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Manage Email Subscription
              </h3>
              <button
                onClick={closeEmailSubscriptionModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {emailSubscriptionUser && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>User:</strong> {emailSubscriptionUser.displayNameOrEmail}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Email:</strong> {emailSubscriptionUser.username}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Current Status:</strong> {emailSubscriptionUser.emailUnsubscribed ? 'Unsubscribed' : 'Subscribed'}
                </p>

                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {emailSubscriptionUser.emailUnsubscribed 
                    ? 'This user is currently unsubscribed from email notifications. You can subscribe them to receive emails again.'
                    : 'This user is currently subscribed to email notifications. You can unsubscribe them to stop receiving emails.'
                  }
                </p>
              </div>
            )}

            {emailSubscriptionStatus.type === 'loading' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-blue-800">{emailSubscriptionStatus.message}</span>
                </div>
              </div>
            )}

            {emailSubscriptionStatus.type === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{emailSubscriptionStatus.message}</p>
              </div>
            )}

            {emailSubscriptionStatus.type === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{emailSubscriptionStatus.message}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              {emailSubscriptionStatus.type === 'success' ? (
                <button
                  onClick={closeEmailSubscriptionModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : emailSubscriptionStatus.type === 'error' ? (
                <button
                  onClick={closeEmailSubscriptionModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={closeEmailSubscriptionModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  {emailSubscriptionUser && (
                    <button
                      onClick={() => confirmEmailSubscription(!emailSubscriptionUser.emailUnsubscribed)}
                      disabled={emailSubscriptionStatus.type === 'loading'}
                      className={`px-4 py-2 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        emailSubscriptionUser.emailUnsubscribed
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-orange-500 hover:bg-orange-600'
                      }`}
                    >
                      {emailSubscriptionUser.emailUnsubscribed ? 'Subscribe' : 'Unsubscribe'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Device Override Modal */}
      {showDeviceOverrideModal && deviceOverrideUser && (
        <DeviceOverrideModal
          user={{
            uid: deviceOverrideUser.key,
            displayName: deviceOverrideUser.displayNameOrEmail,
            email: deviceOverrideUser.username,
            currentDeviceCount: userDevices[deviceOverrideUser.key]?.length || 0,
            subscriptionDeviceLimit: deviceOverrideUser.subscriptionDeviceLimit || 1,
            deviceOverride: deviceOverrideUser.deviceOverride
          }}
          onClose={closeDeviceOverrideModal}
          onSuccess={handleDeviceOverrideSuccess}
        />
      )}
    </div>
  );
};

export default UserServiceTest; 